import { EventEmitter }             from 'events';
import {
  isNullOrUndefined
}                                   from 'rewire-common';
import {
  replace,
  freeze,
}                                   from 'rewire-core';
import { Modal, IModalState, Form } from 'rewire-ui';

export interface IFilterDialogModelState extends IModalState {
  isLoading: boolean;
}

export default abstract class FilterDialogModel<TState = unknown> extends Modal<IFilterDialogModelState & TState> {
  public  form:      Form;
  public  isModal:   boolean;
  private __onEvent: EventEmitter;

  protected constructor(private _title: string) {
    super();
    this.isLoading = false;
    this.form      = this._createForm();
    this.isModal   = true;
    this.action('apply',  this.submit, { variant: 'contained', color: 'primary', disabled: () => this.hasErrors() || !this.hasChanges(), type: 'submit' })
        .action('undo',   this.undo,   { variant: 'contained', disabled: () => !this.hasChanges() })
        .action('close')
        .action('cancel');

    this.__onEvent = new EventEmitter();
  }

  protected async _preload(_filters?: ObjectType): Promise<void> {
  }

  protected async _load(filters: ObjectType): Promise<void> {
    this.form.value = this.mapToForm(filters);
  }

  async load(filters?: ObjectType, isModal: boolean = true) {
    this.isLoading = true;
    this.isModal   = isModal;
    await this._preload(filters);
    if (!isNullOrUndefined(filters)) {
      await this._load(filters);
    } else {
      this._reset();
    }
    this.open();
    this.isLoading = false;
  }

  get onEvent() {
    return this.__onEvent;
  }

  get isLoading(): boolean {
    return this.state.isLoading;
  }

  set isLoading(value: boolean) {
    this.state.isLoading = value;
  }

  get title() {
    return this._title;
  }

  set title(value: string) {
    this._title = value;
  }

  reset(defaultObj?: any) {
    this._reset(defaultObj);
    this.isLoading = false;
  }

  _reset(defaultObj?: any) {
    if (defaultObj) {
      freeze(() => {
        this.form.fields.forEach(field => {
          field.value = defaultObj[field.name];
          field.error = undefined;
        });
        replace((this.form as any)._value, defaultObj);
      });
    } else {
      this.form.clear(true);
    }
  }

  protected hasChanges(): boolean {
    return this.form.hasChanges;
  }

  protected hasErrors(): boolean {
    return this.form.hasErrors;
  }

  protected abstract _createForm(): Form;

  protected mapToSave(): ObjectType { return this.form.toObjectValues(); }

  protected mapToForm(obj: ObjectType): ObjectType { return obj; }

  undo() {
    this.form.revert();
    return false;
  }

  protected abstract _save(filters: ObjectType): Promise<boolean>;

  protected async save(): Promise<boolean> {
    const filters = this.mapToSave();
    if (!await this._save(filters)) {
      return false;
    }
    if (this.isModal) {
      this._reset();
    }
    return true;
  }

  async submit() {
    if (!this.form) return false;
    if (!await this.form.submitAsync(() => this.save())) {
      return false;
    }
    this.onEvent.emit('submit', true);
    return this.isModal;
  }
}
